import { FaArrowLeft } from "react-icons/fa";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SectionHeader from "../common/SectionHeader";
import { getLandingClients } from "../../utils/landingAcf";
import {
  LANDING_WHATSAPP_CONTEXT,
  resolveLandingWhatsAppCta,
} from "../../utils/landingWhatsapp";
import { SECTION_CONTAINER } from "../../utils/layout";
import LandingSection from "./LandingSection";
import ScrollReveal from "./ScrollReveal";
import "../common/HeroSection.css";
import "./LandingClients.css";
import "./LandingSections.css";

const MIN_MARQUEE_ITEMS = 8;

function buildMarqueeItems(items) {
  if (items.length === 0) return [];

  let expanded = [...items];
  while (expanded.length < MIN_MARQUEE_ITEMS) {
    expanded = expanded.concat(items);
  }

  return [...expanded, ...expanded];
}

function ClientLogoSlot({ media, hidden = false }) {
  return (
    <figure className="landing-clients__slot" aria-hidden={hidden || undefined}>
      <div className="landing-clients__slot-inner">
        <div className="landing-clients__logo-wrap">
          <img
            src={media.url}
            alt={hidden ? "" : media.alt || ""}
            className="landing-clients__logo"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </figure>
  );
}

function LandingClients({ acf, resolveMedia }) {
  const { t } = useTranslation();
  const { title, intro, supportingText, ctaText, logos } =
    getLandingClients(acf);

  const logoItems = useMemo(
    () =>
      logos
        .map(({ id, imageId }) => {
          const media = resolveMedia(imageId);
          if (!media?.url) return null;
          return { id, imageId, media };
        })
        .filter(Boolean),
    [logos, resolveMedia],
  );

  const marqueeItems = useMemo(
    () => buildMarqueeItems(logoItems),
    [logoItems],
  );

  if (!title && !intro && !supportingText && logoItems.length === 0) return null;

  const ctaLabel = ctaText || t("landing.clients.cta");
  const ctaLinkResolved = resolveLandingWhatsAppCta(t, LANDING_WHATSAPP_CONTEXT.CLIENTS, {
    label: ctaLabel,
  });
  const stripLabel = t("landing.clients.stripLabel");
  const marqueeDuration = Math.max(28, logoItems.length * 6);

  return (
    <LandingSection className="landing-clients">
      <div className={`${SECTION_CONTAINER} landing-clients__inner`}>
        {(title || intro) && (
          <ScrollReveal className="landing-section__intro">
            <SectionHeader
              title={title || undefined}
              subtitle={intro || undefined}
              showAccent
              centered
              titleId="landing-clients-title"
              containerClass=""
            />
          </ScrollReveal>
        )}

        {logoItems.length > 0 ? (
          <ScrollReveal
            className="landing-section__body landing-clients__showcase"
            aria-labelledby={title ? "landing-clients-title" : undefined}
          >
            <div className="landing-clients__showcase-head">
              <p className="landing-clients__strip-label">{stripLabel}</p>
            </div>

            <div
              className="landing-clients__marquee"
              role="region"
              aria-label={stripLabel}
            >
              <div className="landing-clients__viewport">
                <div
                  className="landing-clients__track"
                  style={{ "--marquee-duration": `${marqueeDuration}s` }}
                >
                  {marqueeItems.map(({ id, imageId, media }, index) => (
                    <ClientLogoSlot
                      key={`client-logo-${id}-${imageId}-${index}`}
                      media={media}
                      hidden={index >= marqueeItems.length / 2}
                    />
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>
        ) : null}

        {(supportingText || ctaLabel) && (
          <ScrollReveal as="footer" className="landing-clients__footer">
            {supportingText ? (
              <p className="landing-clients__supporting">{supportingText}</p>
            ) : null}

            <a
              href={ctaLinkResolved.href}
              className="hero-btn hero-btn--primary landing-clients__cta-btn"
              target={ctaLinkResolved.target}
              rel={ctaLinkResolved.rel}
            >
              <span className="hero-btn__label">{ctaLabel}</span>
              <span className="hero-btn__icon-wrap" aria-hidden="true">
                <FaArrowLeft className="hero-btn__icon" />
              </span>
            </a>
          </ScrollReveal>
        )}
      </div>
    </LandingSection>
  );
}

export default LandingClients;
